import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "./sentry";

export type LogLevel = "error" | "warning" | "info";
export type LogCategory =
  | "checkout_validation"
  | "supabase_error"
  | "payment_error"
  | "auth_error"
  | "api_error"
  | "js_runtime_error"
  | "unhandled_promise"
  | "custom";

export interface LogSystemErrorParams {
  level?: LogLevel;
  category: LogCategory;
  message: string;
  eventId?: string | null;
  userEmail?: string | null;
  details?: Record<string, unknown>;
}

// Memory throttle to prevent infinite log loops if a continuous error occurs
const recentLogHashes = new Set<string>();

/**
 * Fires a non-blocking background log entry into public.system_logs table.
 */
export async function logSystemError({
  level = "error",
  category,
  message,
  eventId = null,
  userEmail = null,
  details = {},
}: LogSystemErrorParams): Promise<void> {
  try {
    const url = typeof window !== "undefined" ? window.location.href : undefined;
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : undefined;

    // Deduplicate same error within 5 seconds
    const hash = `${category}:${message}:${url}`;
    if (recentLogHashes.has(hash)) return;
    recentLogHashes.add(hash);
    setTimeout(() => recentLogHashes.delete(hash), 5000);

    // Envia para o Sentry com tags e contexto
    if (level === "error") {
      Sentry.captureMessage(message, {
        level: "error",
        tags: { category, eventId: eventId || undefined },
        extra: { details, url, userEmail },
      });
    } else if (level === "warning") {
      Sentry.captureMessage(message, {
        level: "warning",
        tags: { category, eventId: eventId || undefined },
        extra: { details, url, userEmail },
      });
    }

    // Fire-and-forget insert
    // Contrato local tipado; disponibilidade remota depende da migração publicada.
    await (supabase).from("system_logs").insert({
      level,
      category,
      message,
      event_id: eventId,
      user_email: userEmail,
      details: JSON.parse(JSON.stringify(details ?? {})),
      url,
      user_agent: userAgent,
    });
  } catch (err) {
    // Fail silently so logging never breaks primary user actions
    console.warn("Failed to record system log:", err);
  }
}

/**
 * Helper specifically for logging checkout validation failures (e.g. invalid PMI ID, wrong promo code)
 */
export async function logValidationFailure({
  eventId,
  userEmail,
  questionId,
  questionTitle,
  attemptedValue,
  reason = "invalid_option",
}: {
  eventId?: string | null;
  userEmail?: string | null;
  questionId?: string;
  questionTitle?: string;
  attemptedValue?: string;
  reason?: string;
}): Promise<void> {
  const message = `Validação recusada em '${questionTitle || "Formulário"}': "${attemptedValue || ""}"`;

  return logSystemError({
    level: "warning",
    category: "checkout_validation",
    message,
    eventId,
    userEmail,
    details: {
      questionId,
      questionTitle,
      attemptedValue,
      reason,
      timestamp: new Date().toISOString(),
    },
  });
}

/**
 * Helper for logging Supabase API / RPC failures
 */
export async function logSupabaseError(
  actionName: string,
  error: unknown,
  context: Record<string, unknown> = {}
): Promise<void> {
  const detail = error !== null && typeof error === "object" ? error as Record<string, unknown> : {};
  const message = `Erro Supabase [${actionName}]: ${detail.message || detail.hint || String(error)}`;
  return logSystemError({
    level: "error",
    category: "supabase_error",
    message,
    details: {
      actionName,
      code: detail.code,
      details: detail.details,
      hint: detail.hint,
      ...context,
    },
  });
}

/**
 * Helper for logging Payment & Gateway errors
 */
export async function logPaymentError(
  actionName: string,
  error: unknown,
  context: Record<string, unknown> = {}
): Promise<void> {
  const detail = error !== null && typeof error === "object" ? error as Record<string, unknown> : {};
  const message = `Falha no Pagamento [${actionName}]: ${detail.message || String(error)}`;
  return logSystemError({
    level: "error",
    category: "payment_error",
    message,
    details: {
      actionName,
      ...context,
    },
  });
}

/**
 * Registers global browser error listeners to catch 100% of uncaught JS crashes & Promise rejections
 */
export function setupGlobalErrorLogging(): void {
  if (typeof window === "undefined") return;

  // 1. Catch uncaught Javascript exceptions
  window.addEventListener("error", (event) => {
    // Ignore script load errors from external plugins or cross-origin scripts with no message
    if (!event.message) return;

    logSystemError({
      level: "error",
      category: "js_runtime_error",
      message: `Erro JS: ${event.message}`,
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      },
    });
  });

  // 2. Catch unhandled Promise rejections (failed async calls, fetch errors, uncaught promises)
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason?.message || (typeof reason === "string" ? reason : "Promise rejeitada sem tratamento");

    logSystemError({
      level: "error",
      category: "unhandled_promise",
      message: `Promessa não tratada: ${message}`,
      details: {
        reason: typeof reason === "object" ? JSON.stringify(reason) : String(reason),
        stack: reason?.stack,
      },
    });
  });
}
